/**
 * utils/clientIntake.ts — FRONTEND MIRROR of supabase/functions/server/clientIntakeCore.ts.
 *
 * Generated: do not edit by hand; run scripts/sync-discovery-grid-mirror.sh.
 * tests/server/clientIntakeCore_test.ts asserts parity.
 */


/** The variables a consultant may use in the invitation message. */
export const INVITATION_VARIABLES: readonly string[] = [
  "companyName",
  "adminName",
  "consultantName",
  "consultantEmail",
  "loginLink",
  "firmName",
];

export const MAX_INVITATION_LENGTH = 5000;

/** `{{ name }}` replaced by its value; unknown names are left as written. */
export function renderInvitation(template: string, vars: Record<string, string | undefined>): string {
  return String(template ?? "").replace(/\{\{\s*([a-zA-Z]+)\s*\}\}/g, (whole, name: string) =>
    INVITATION_VARIABLES.includes(name) && vars[name] !== undefined ? String(vars[name]) : whole
  );
}

/** Whether the message already carries the sign-in link (else the email adds its own button and link). */
export function mentionsLoginLink(template: string): boolean {
  return /\{\{\s*loginLink\s*\}\}/.test(String(template ?? ""));
}

const escapeHtml = (t: string) =>
  t.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;").replace(/'/g, "&#39;");

/**
 * Plain text as email HTML for inside the email's intro paragraph: escaped,
 * newlines as breaks (a blank line stays a blank line), links clickable.
 */
export function invitationHtml(text: string): string {
  const safe = escapeHtml(String(text ?? "").trim()).replace(/(https?:\/\/[^\s<]+)/g, '<a href="$1" style="color:#2563eb;">$1</a>');
  return safe.replace(/\r?\n/g, "<br>");
}

/** A message the server will send: trimmed, not too long; an empty one means the standard email. */
export function checkInvitationMessage(body: unknown): { ok: true; message: string | null } | { ok: false; error: string } {
  if (body === undefined || body === null) return { ok: true, message: null };
  if (typeof body !== "string") return { ok: false, error: "The invitation message must be text" };
  const message = body.trim();
  if (message.length > MAX_INVITATION_LENGTH) return { ok: false, error: `The invitation message is at most ${MAX_INVITATION_LENGTH} characters` };
  return { ok: true, message: message || null };
}

/** The platform template (TemplateSelector id) a sales-notes extraction recommends ("jira" means the Atlassian template). */
export function platformTemplateFor(recommended: unknown): string | null {
  if (typeof recommended !== "string") return null;
  const r = recommended.trim().toLowerCase();
  if (["jira", "jsm", "atlassian", "jira service management"].includes(r)) return "atlassian";
  if (["servicenow", "ivanti", "freshservice"].includes(r)) return r;
  if (["bmc", "bmc helix", "bmc-helix", "helix"].includes(r)) return "bmc-helix";
  return null;
}

const LIST_FIELDS = ["painPoints", "objectives", "stakeholders", "technicalEnvironment"] as const;
const TEXT_FIELDS = ["industry", "timeline", "budgetIndicators", "currentSystem", "teamSize", "recommendedPlatform"] as const;
const MAX_LIST = 25;
const MAX_TEXT = 500;

/**
 * What the AI pulled out of the sales notes, as stored on the company and
 * used to seed its objectives: the known fields only, text trimmed, lists as
 * lists of non-empty strings. The browser sends it back on create, so it is
 * checked like any other input.
 */
export function normalizeBdmExtraction(data: unknown): Record<string, unknown> | null {
  if (!data || typeof data !== "object" || Array.isArray(data)) return null;
  const src = data as Record<string, unknown>;
  const out: Record<string, unknown> = {};
  for (const key of TEXT_FIELDS) {
    const v = src[key];
    if (typeof v === "string" && v.trim()) out[key] = v.trim().slice(0, MAX_TEXT);
    else if (typeof v === "number") out[key] = String(v);
  }
  for (const key of LIST_FIELDS) {
    const v = src[key];
    const list = Array.isArray(v) ? v : typeof v === "string" ? [v] : [];
    const clean = list
      .filter((x) => typeof x === "string" && x.trim())
      .map((x) => (x as string).trim().slice(0, MAX_TEXT))
      .slice(0, MAX_LIST);
    if (clean.length) out[key] = clean;
  }
  return Object.keys(out).length ? out : null;
}
